import { Menu } from '@base-ui/react/menu';
import styled from '@emotion/styled';

import { typographyVariantStyles } from '../../typography-variants';
import type { MenuItemTone } from './types';

/**
 * The popup floats above panels that sit on `background.canvas` / `raised`, so it
 * uses `overlay` — the lightest neutral — plus a border and shadow to read as a
 * separate layer rather than a hole punched in the panel.
 */
export const StyledPopup = styled(Menu.Popup, {
  shouldForwardProp: (prop) => prop !== '$minWidth',
})<{ $minWidth: number }>(({ theme, $minWidth }) => ({
  minWidth: $minWidth,
  maxHeight: 'min(420px, var(--available-height))',
  overflowY: 'auto',
  boxSizing: 'border-box',
  padding: theme.spacing[4],

  background: theme.colors.background.overlay,
  border: `1px solid ${theme.colors.border.default}`,
  borderRadius: theme.radius[12],
  boxShadow: theme.shadows.sm,
  outline: 'none',

  transformOrigin: 'var(--transform-origin)',
  transition: 'opacity 150ms ease-out, transform 150ms ease-out',

  '&[data-starting-style], &[data-ending-style]': {
    opacity: 0,
    transform: 'scale(0.96)',
  },

  '@media (prefers-reduced-motion: reduce)': {
    transition: 'opacity 1ms linear',
    '&[data-starting-style], &[data-ending-style]': {
      transform: 'none',
    },
  },
}));

export const StyledItem = styled(Menu.Item, {
  shouldForwardProp: (prop) => prop !== '$tone' && prop !== '$hasDescription',
})<{ $tone: MenuItemTone; $hasDescription: boolean }>(({ theme, $tone, $hasDescription }) => {
  const isDestructive = $tone === 'destructive';

  return {
    display: 'flex',
    alignItems: $hasDescription ? 'flex-start' : 'center',
    gap: theme.spacing[8],
    width: '100%',
    boxSizing: 'border-box',
    padding: $hasDescription
      ? `${theme.spacing[8]}px ${theme.spacing[8]}px`
      : `6px ${theme.spacing[8]}px`,
    borderRadius: theme.radius[8],

    cursor: 'pointer',
    userSelect: 'none',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',

    // `error.text` (red 500) is only 4.22:1 on the popup surface. `error.hover`
    // (red 400) reaches 5.06:1. Same token defect TopicPill/tone.ts documents.
    color: isDestructive ? theme.colors.error.hover : theme.colors.text.primary,
    ...typographyVariantStyles.body3,
    fontWeight: 500,

    transition: 'background-color 150ms ease-out, color 150ms ease-out',

    // Base UI marks the active row with data-highlighted for both pointer hover and
    // keyboard navigation, so one rule covers hover and focus. Styling :hover
    // separately would desync the two.
    //
    // The tint alone cannot carry this state accessibly: no single white alpha over
    // the popup satisfies both SC 1.4.11 (>=3:1 against the surface, needs ~white[40])
    // and 4.5:1 for the 14px label on top of it (white[40] drops the label to 4.09).
    // So a moderate tint keeps the label crisp and a hairline outline carries the
    // state indicator at 7.24:1. Without it, keyboard users cannot see their position.
    '&[data-highlighted]': {
      backgroundColor: isDestructive ? theme.colors.error.subtle : theme.colors.white[16],
      outline: `1px solid ${theme.colors.border.focus}`,
      outlineOffset: -1,
    },

    '&[data-disabled]': {
      color: theme.colors.text.disabled,
      cursor: 'not-allowed',
      backgroundColor: 'transparent',
    },
  };
});

export const StyledItemIcon = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 16,
  height: 16,
  marginTop: 1,
  color: 'currentColor',

  '& svg': {
    width: '100%',
    height: '100%',
  },

  '[data-disabled] &': {
    color: theme.colors.icon.disabled,
  },
}));

export const StyledItemText = styled('span')({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  flex: 1,
});

export const StyledItemLabel = styled('span')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const StyledItemDescription = styled('span')(({ theme }) => ({
  ...typographyVariantStyles.body4,
  fontWeight: 400,
  color: theme.colors.text.secondary,
  whiteSpace: 'normal',
}));

export const StyledItemTrailing = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexShrink: 0,
  marginLeft: 'auto',
  paddingLeft: theme.spacing[8],
  ...typographyVariantStyles.body4,
  fontWeight: 400,
  color: theme.colors.text.secondary,

  '& svg': {
    width: 16,
    height: 16,
    color: theme.colors.icon.brand,
  },
}));

export const StyledSeparator = styled(Menu.Separator)(({ theme }) => ({
  height: 1,
  margin: `${theme.spacing[4]}px ${theme.spacing[8]}px`,
  // Not `border.default` — at #262626 on the #232222 popup it is invisible. The
  // popup is the lightest surface in the stack, so separators have to lift off it
  // rather than sit at the same value as borders on darker panels.
  backgroundColor: theme.colors.white[12],
  border: 'none',
}));

export const StyledGroupLabel = styled(Menu.GroupLabel)(({ theme }) => ({
  display: 'block',
  padding: `${theme.spacing[8]}px ${theme.spacing[8]}px ${theme.spacing[4]}px`,
  ...typographyVariantStyles.body4,
  fontWeight: 500,
  color: theme.colors.text.secondary,
}));
